/**
 * AIChat Page
 * Main page for AI-powered neighborhood recommendations
 * Integrates ChatInterface and RecommendationCard components
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ChatInterface from '../components/AI/ChatInterface';
import RecommendationCard from '../components/AI/RecommendationCard';
import InteractiveMap, { isInteractiveMapAvailable } from '../components/AI/InteractiveMap';
import LogoIcon from '../components/Brand/LogoIcon';
// Round 6 (2026-05-05 LG feedback): Compare/Explore now render IN the right
// panel instead of replacing the whole AIChat page. We import the existing
// page components and pass them embedded-mode props. Their standalone routes
// (/areas/:slug/compare, /areas/:slug/explore) keep working as deep-link
// fallbacks for backward compat — both files now support both render modes.
import CompareAreas from './CompareAreas';
import ExploreMore from './ExploreMore';
import { clearSession, getSessionId, generateSessionId } from '../services/aiApi';
import './AIChat.css';

// US state name → 2-letter code (added 2026-04-25 for feedback API compat).
// The backend's disambiguation handler stores `extractedData.location.state` as
// the FULL state name ("Texas") when available, dropping the parallel
// `stateShort` field. The feedback API requires a 2-char code, so we map
// here at the consumer rather than patching the conversation flow per
// the v2-refactor moratorium on multiAgentRoutes patches.
const STATE_NAME_TO_CODE = {
  'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
  'colorado':'CO','connecticut':'CT','delaware':'DE','district of columbia':'DC',
  'florida':'FL','georgia':'GA','hawaii':'HI','idaho':'ID','illinois':'IL',
  'indiana':'IN','iowa':'IA','kansas':'KS','kentucky':'KY','louisiana':'LA',
  'maine':'ME','maryland':'MD','massachusetts':'MA','michigan':'MI','minnesota':'MN',
  'mississippi':'MS','missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV',
  'new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY',
  'north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK','oregon':'OR',
  'pennsylvania':'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD',
  'tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT','virginia':'VA',
  'washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY'
};

/**
 * Resolve any state representation to a 2-letter uppercase code.
 * Handles: "TX" → "TX", "tx" → "TX", "Texas" → "TX", "texas" → "TX".
 * Returns null if input can't be resolved.
 */
const toStateCode = (raw) => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const code = STATE_NAME_TO_CODE[trimmed.toLowerCase()];
  return code || null;
};

// Issue 3 / Option β (2026-05-06 evening). Cities for which we have
// seeded resident reviews in ChromaDB. The discovery feed surfaces a
// "Reading N resident reviews" card only when the user's city is in
// this set — otherwise that card would lie.
//
// Source of truth: server/scripts/seed-reviews.js. Update both files
// together. Counts mirror the seed file (3 reviews per neighborhood).
const SEEDED_REVIEW_CITIES = {
  austin: 30,
  'los angeles': 9,
  portland: 9,
  houston: 9,
  'new york': 9
};
const reviewsCountForCity = (city) => {
  if (!city || typeof city !== 'string') return 0;
  return SEEDED_REVIEW_CITIES[city.trim().toLowerCase()] || 0;
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5050';

const AIChat = () => {
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // Sunday polish 2026-05-03: track conversation context (esp. location) so we
  // can render a live Google Maps preview in the right panel during the chat.
  // Once recommendations are ready, the cards replace the map (current behavior).
  const [currentContext, setCurrentContext] = useState(null);

  // Path B build (2026-05-06 evening): candidate neighborhoods fetched as
  // soon as the user's city is confirmed, so the right-panel map can drop
  // pins throughout the conversation rather than only at search time.
  // Shape: { candidates: [{ name, lat, lng }] } — empty array on miss.
  // Keyed-by-city in state so we don't re-fetch when other context updates
  // arrive after the candidates are already loaded for this city.
  const [candidates, setCandidates] = useState([]);
  const [candidatesCity, setCandidatesCity] = useState(null);

  // JS SDK migration (2026-05-06 evening, Path B v2): when the Maps JS
  // SDK can't load (no key, ad-blocker, network failure), InteractiveMap
  // calls back here so we render the existing Static Maps fallback. One
  // boolean — once set true, we don't try again for the rest of the
  // session. Reset by a session restart (Start over button → page
  // reload → fresh state).
  const [interactiveMapFailed, setInteractiveMapFailed] = useState(false);

  // Round 6 (2026-05-05 LG feedback): the right panel is now a switch between
  // four views. The chat on the left is preserved across all of them.
  //   'recommendations' — default once recommendations come in (cards list)
  //   'compare'         — Compare Areas embedded view (CompareAreas component)
  //   'explore'         — Explore More embedded view (ExploreMore component)
  // Each non-default view also stores `panelTarget` — the payload from the
  // recommendation card the user clicked (item, city, region, image, etc.)
  // — which is passed down as props to the embedded component.
  const [panelView, setPanelView] = useState('recommendations');
  const [panelTarget, setPanelTarget] = useState(null);

  const handlePanelOpen = (view, payload) => {
    setPanelView(view);
    setPanelTarget(payload);
    // Scroll the right column to top so the new view starts at the top.
    // Use a microtask so React commits the new content first.
    setTimeout(() => {
      const col = document.querySelector('.recommendations-section');
      if (col && col.scrollTo) col.scrollTo({ top: 0, behavior: 'auto' });
    }, 0);
  };

  const handlePanelClose = () => {
    setPanelView('recommendations');
    setPanelTarget(null);
  };

  // sessionStorage key — added 2026-04-25 so recommendations survive when the
  // user side-trips to /submit-review (or any other internal route) and returns
  // to /ai. Without persistence, AIChat remounts with null recs = blank screen.
  // sessionStorage (not localStorage) so it clears on tab close, matching
  // "my search is for this session" UX.
  const RECS_STORAGE_KEY = 'ai_recommendations';

  // Initialize session ID + restore cached recommendations on mount
  useEffect(() => {
    let currentSessionId = getSessionId();
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      localStorage.setItem('ai_session_id', currentSessionId);
    }
    setSessionId(currentSessionId);

    // Hydrate recommendations from sessionStorage (only if same session).
    // The wrapped record stores sessionId so we don't show stale recs from
    // a previous user's session in case sessionStorage somehow leaks.
    try {
      const cachedRaw = sessionStorage.getItem(RECS_STORAGE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.sessionId === currentSessionId && cached.data) {
          setRecommendations(cached.data);
        }
      }
    } catch (err) {
      console.warn('[AIChat] Failed to restore cached recommendations:', err.message);
    }
  }, []);

  // Path B (2026-05-06 evening): early-fetch candidate neighborhoods as
  // soon as the user's city is CONFIRMED (post-disambiguation). The pins
  // these produce drive the right-panel map markers throughout the rest
  // of the conversation. Re-fetches if the user pivots cities mid-chat.
  // Backend caches per (city,state) for 7 days, so repeat sessions in
  // the same city pay $0.
  useEffect(() => {
    const loc = currentContext?.location;
    if (!loc || loc.confirmed !== true || !loc.value) return;
    if (loc.type === 'state') return; // too broad, no candidates worth fetching

    // Skip if we already have candidates for this same city.
    const cityKey = `${(loc.value || '').toLowerCase()}|${(loc.state || '').toLowerCase()}`;
    if (candidatesCity === cityKey) return;

    const ctrl = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({ city: loc.value });
        if (loc.state) params.set('state', toStateCode(loc.state) || loc.state);
        const res = await fetch(
          `${API_BASE}/api/places/candidates?${params.toString()}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) {
          console.warn(
            `[AIChat] candidates fetch returned ${res.status} — pins will not render. ` +
            (res.status === 503
              ? 'Likely missing GOOGLE_MAPS_API_KEY or OPENAI_API_KEY on backend.'
              : '')
          );
          setCandidates([]);
          setCandidatesCity(cityKey); // mark as attempted so we don't retry-loop
          return;
        }
        const json = await res.json();
        setCandidates(json.candidates || []);
        setCandidatesCity(cityKey);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('[AIChat] candidates fetch error:', e.message);
          setCandidates([]);
          setCandidatesCity(cityKey);
        }
      }
    })();
    return () => ctrl.abort();
  }, [
    currentContext?.location?.value,
    currentContext?.location?.state,
    currentContext?.location?.confirmed,
    candidatesCity
  ]);

  const handleRecommendationsReady = (data) => {
    setRecommendations(data);
    // Persist to sessionStorage immediately so a side-trip to /submit-review
    // and back doesn't wipe the user's results. We wrap with sessionId so we
    // can validate freshness on rehydrate.
    try {
      const sid = sessionId || getSessionId();
      sessionStorage.setItem(
        RECS_STORAGE_KEY,
        JSON.stringify({ sessionId: sid, data, savedAt: Date.now() })
      );
    } catch (err) {
      // sessionStorage can throw on quota / private mode — non-fatal.
      console.warn('[AIChat] Failed to cache recommendations:', err.message);
    }
  };

  const handleStartOver = async () => {
    setIsLoading(true);
    await clearSession();
    setRecommendations(null);
    // Clear cached recs + chat history so neither reappears after reload.
    // ai_chat_messages key added round 6 #6 (2026-05-05) — without this
    // clear, chat persistence would re-hydrate the prior chat on reload.
    try { sessionStorage.removeItem(RECS_STORAGE_KEY); } catch {}
    try { sessionStorage.removeItem('ai_chat_messages'); } catch {}
    setIsLoading(false);
    // Reload page to reset chat
    window.location.reload();
  };

  return (
    <div className="ai-chat-page">
      {/* ─── HEADER (splash-style pill nav) ──────────────────────────
          Sunday polish 2026-05-03 (round 2): emoji 🏡 logo replaced with
          shared LogoIcon SVG and the header restyled as a floating pill
          to match the splash design language.

          Layout: [Logo] ←→ [Share review · Start Over]
          The "Try Now" splash CTA is replaced with "Start Over" since
          the user is already inside the app. */}
      <header className="ai-nav-wrap">
        <nav className="ai-nav">
          <Link to="/" className="ai-logo">
            <LogoIcon size={26} />
            <span className="ai-logo-text">
              LocalGenie<span className="ai-logo-accent">.ai</span>
            </span>
          </Link>
          <div className="ai-nav-actions">
            <Link to="/submit-review" className="ai-nav-link">
              Share a review
            </Link>
            <button
              className="ai-nav-cta"
              onClick={handleStartOver}
              disabled={isLoading}
              aria-label="Start over with a new conversation"
            >
              {isLoading ? 'Resetting...' : 'Start over'}
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="page-main">
        <div className="content-container">
          {/* Left: Chat Interface */}
          <div className="chat-section">
            {sessionId ? (
              <ChatInterface
                onRecommendationsReady={handleRecommendationsReady}
                onContextUpdate={setCurrentContext}
                sessionId={sessionId}
                recommendationsReady={!!recommendations}
              />
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading...
              </div>
            )}
          </div>

          {/* Right: panelView switch — Round 6 (LG feedback 2026-05-05).
              Compare/Explore now render IN this panel (chat preserved on left)
              instead of replacing the whole page. The "Back to results" pill
              returns the panel to the regular flow (map preview during chat
              → recommendation cards once ready). */}
          <div className="recommendations-section">
            {(panelView === 'compare' || panelView === 'explore') && panelTarget && (
              <div className="ai-panel-embedded">
                <button
                  type="button"
                  className="ai-panel-back"
                  onClick={handlePanelClose}
                  aria-label="Back to results"
                >
                  ← Back to results
                </button>
                {panelView === 'compare' && (
                  <CompareAreas
                    embedded={true}
                    propsItem={panelTarget.item}
                    propsCity={panelTarget.city}
                    propsHeroImage={panelTarget.heroImage}
                    onClose={handlePanelClose}
                  />
                )}
                {panelView === 'explore' && (
                  <ExploreMore
                    embedded={true}
                    propsItem={panelTarget.item}
                    propsCity={panelTarget.city}
                    propsHeroImage={panelTarget.heroImage}
                    onClose={handlePanelClose}
                  />
                )}
              </div>
            )}
            {panelView === 'recommendations' && (recommendations ? null : (() => {
              // Sunday polish 2026-05-03: 3-state right panel:
              //   1. Recommendations available → cards (handled in else branch below)
              //   2. Location detected (mid-chat) → Google Maps iframe preview
              //   3. No location yet → original empty state
              const detectedCity = currentContext?.location?.value;
              const detectedState = currentContext?.location?.state;
              if (detectedCity) {
                // Issue 3 / Option β (2026-05-06 evening): swapped the legacy
                // public Google Maps iframe for our backend Static Maps proxy
                // (`/api/places/staticmap`). The iframe was decorative — no
                // way to drop a workplace pin programmatically. Static Maps
                // returns a real PNG with markers encoded as URL params, so
                // when the backend captures workLocationCoords we can
                // immediately overlay an "Apple HQ" pin on the same map.
                //
                // Cost: $2/1k after Google's 100k-free-per-month tier. For
                // portfolio traffic this is effectively free; see DEFERRED
                // doc Tier 2.2/2.3 cost analysis section for the breakdown
                // pattern.
                //
                // We pass `center=City, ST` and let Google's geocoder pick
                // the right pan/zoom — same pattern the Find Place flow uses
                // for Street View. Markers are appended only when we have
                // workLocationCoords.
                const query = detectedState
                  ? `${detectedCity}, ${detectedState}`
                  : detectedCity;

                const workLat = currentContext?.workLocationCoords?.lat;
                const workLng = currentContext?.workLocationCoords?.lng;
                const hasWorkplaceCoords = (
                  typeof workLat === 'number' &&
                  typeof workLng === 'number'
                );

                // Path B build (2026-05-06 evening): the static map now
                // shows MULTIPLE pins — one per candidate neighborhood
                // (fetched as soon as location was confirmed, see useEffect
                // earlier in this component) plus the workplace pin when
                // present. Google's Static Maps API auto-fits the viewport
                // to include all markers when no `center` is specified, so
                // we omit center entirely and let the API frame the pins.
                //
                // Marker styling: candidate pins use a lighter accent
                // color and unlabeled drop shape; the workplace pin uses
                // the dark indigo + "W" label so it stands apart from the
                // candidate cluster.
                // Path B follow-up (2026-05-06 evening): bumped from 600x300
                // (2:1 letterbox) to 600x420 (~10:7) to fill more of the
                // right panel — LG flagged the original 2:1 ratio left a
                // lot of blank space below the map. Static Maps caps each
                // dimension at 640 in the free tier; 600x420 stays well
                // within that. Browser CSS aspect-ratio updated to match.
                const staticMapParams = new URLSearchParams({
                  size: '600x420',
                  maptype: 'roadmap',
                });
                // If we have neither candidates nor workplace coords, fall
                // back to centering on the city — Google needs SOME anchor.
                const hasAnyMarker = candidates.length > 0 || hasWorkplaceCoords;
                if (!hasAnyMarker) {
                  staticMapParams.set('center', query);
                  staticMapParams.set('zoom', '11');
                }
                // Candidate pins — accent color, no label, smaller cluster.
                // Color hex 0x7F77DD = mid indigo (Purple 400) so the
                // workplace pin in dark 0x1F1A4A reads as more important.
                for (const c of candidates) {
                  if (typeof c?.lat === 'number' && typeof c?.lng === 'number') {
                    staticMapParams.append(
                      'markers',
                      `${c.lat},${c.lng},,0x7F77DD`
                    );
                  }
                }
                // Workplace pin — dark indigo with "W" label so it pops out
                // of the candidate cluster as the user's anchor point.
                if (hasWorkplaceCoords) {
                  staticMapParams.append(
                    'markers',
                    `${workLat},${workLng},W,0x1F1A4A`
                  );
                }
                const mapsUrl = `${API_BASE}/api/places/staticmap?${staticMapParams.toString()}`;

                // Round 6 #60 (2026-05-05): hybrid right-panel.
                // Map iframe stays as the visual backdrop, but a "What I'm
                // learning" preferences card overlays on top, filling in
                // progressively as ConversationAgent extracts data. Each row
                // only renders if the corresponding field is captured —
                // partial-fill is the whole point.
                //
                // Field reads are defensive across two storage shapes the
                // backend has used during the rebuild (top-level fields vs.
                // a nested `location: { value, state }` object). We look in
                // both and use whichever exists.
                const ctxCity =
                  currentContext?.city ||
                  currentContext?.location?.value ||
                  detectedCity;
                // Round 6 / Tier 2.4 (2026-05-06): when user is WFH/remote/retired/
                // student, the workplace row never filled in (because there's no
                // workLocation), capping readiness at ~83%. Now we show a
                // sentinel value so the user gets feedback that we captured
                // their employment status, and the readiness card hits 100%.
                const ctxEmploymentStatus =
                  currentContext?.demographics?.employmentStatus ||
                  currentContext?.employmentStatus ||
                  null;
                const employmentDisplayMap = {
                  remote: 'Remote / WFH',
                  retired: 'Retired',
                  student: 'Student'
                };
                // 2026-05-06 evening: workLocation comes off the backend as a
                // plain STRING ("Apple Inc.", "12545 Riata Vista Cir") in the
                // most common path — see ConversationAgent.js mergeContext().
                // The pre-existing object-form lookups (.value / .name) were
                // dead code for the string path, so the row stayed empty.
                // This was hidden until Option β surfaced the workplace in the
                // discovery feed and exposed it.
                const rawWorkLocation = currentContext?.workLocation;
                const ctxWorkplace =
                  (typeof rawWorkLocation === 'string' && rawWorkLocation.trim()
                    ? rawWorkLocation.trim()
                    : null) ||
                  rawWorkLocation?.value ||
                  rawWorkLocation?.name ||
                  currentContext?.workplace?.value ||
                  employmentDisplayMap[String(ctxEmploymentStatus).toLowerCase()] ||
                  null;
                // Short label for the workplace — used in the discovery card
                // and the map badge. The backend stores workLocation as the
                // full disambiguation result (e.g., "12545 Riata Vista Cir,
                // Austin, TX 78727, USA"), too verbose for an inline label.
                // The first comma-segment is almost always the street address
                // or company name — recognizable on its own.
                const shortWorkplace = (ctxWorkplace || '')
                  .split(',')[0]
                  .trim();
                const ctxBudget = currentContext?.budget;
                const ctxBudgetType = currentContext?.budgetType;
                const ctxRelStatus =
                  currentContext?.demographics?.relationshipStatus ||
                  currentContext?.demographic ||
                  null;
                const ctxChildren = currentContext?.children;
                const ctxHousingIntent = currentContext?.housingIntent;
                const ctxPriorities = currentContext?.priorities || [];

                // Pretty labels for the captured values. Mirrors the simpler
                // mappings used in ChatInterface.jsx — full duplicates are
                // overkill here since this card displays at most ~6 rows.
                const prettyDemographic = (k) => {
                  if (!k) return null;
                  const map = {
                    solo: 'Solo', single: 'Solo',
                    partner: 'With partner', couple: 'Couple', married: 'Married',
                    family: 'Family',
                    retired: 'Retired', student: 'Student'
                  };
                  return map[String(k).toLowerCase()] ||
                    String(k).charAt(0).toUpperCase() + String(k).slice(1);
                };
                const prettyHousing = (k) => {
                  if (!k) return null;
                  return k === 'rent' ? 'Rent' : k === 'buy' ? 'Buy' : k;
                };
                const prettyPriority = (k) => {
                  if (!k || typeof k !== 'string') return null;
                  // Take "proximity_to_work" → "Proximity to work"
                  return k
                    .replace(/_/g, ' ')
                    .replace(/^./, (m) => m.toUpperCase());
                };

                // Build the rows. Filter out any nulls so we only render
                // captured data — empty rows would look broken.
                const prefRows = [
                  {
                    icon: '📍',
                    label: 'City',
                    value: detectedState ? `${ctxCity}, ${detectedState}` : ctxCity
                  },
                  ctxWorkplace && {
                    icon: '💼',
                    label: 'Workplace',
                    value: ctxWorkplace
                  },
                  ctxBudget && {
                    icon: '💰',
                    label: 'Budget',
                    value: typeof ctxBudget === 'number'
                      ? `$${ctxBudget.toLocaleString()}${ctxBudgetType === 'monthly_rent' ? '/mo' : ''}`
                      : String(ctxBudget)
                  },
                  ctxRelStatus && {
                    icon: '🙋',
                    label: 'Family',
                    value: ctxChildren > 0
                      ? `${prettyDemographic(ctxRelStatus)} · ${ctxChildren} kid${ctxChildren === 1 ? '' : 's'}`
                      : prettyDemographic(ctxRelStatus)
                  },
                  ctxHousingIntent && {
                    icon: '🔑',
                    label: 'Looking to',
                    value: prettyHousing(ctxHousingIntent)
                  },
                  ctxPriorities.length > 0 && {
                    icon: '✨',
                    label: 'Priorities',
                    value: ctxPriorities.slice(0, 4).map(prettyPriority).filter(Boolean).join(' · ')
                  }
                ].filter(Boolean);

                // Approximate readiness — caps at 6 fields. Non-scientific
                // but gives the user visible progress as they chat.
                const readinessPct = Math.min(
                  100,
                  Math.round((prefRows.length / 6) * 100)
                );

                // Path B (2026-05-06 evening): the discovery cards array
                // was removed — the right panel now communicates "AI is
                // working" via real candidate pins on the map plus the
                // captured prefs card, both of which carry actual signal.
                // Variables below are still computed because the map URL
                // builder + workplace badge consume some of them.

                return (
                  <div className="map-preview-state map-preview-overlay-mode">
                    {/* Option C (2026-05-06 evening): full-bleed map
                        with two floating glassmorphism overlay cards.
                        Layout structure:
                          - .map-preview-static-wrap fills the entire
                            panel (absolute, inset 0)
                          - .map-preview-overlay-considering floats top-left
                          - .map-preview-overlay-learning floats bottom-right
                          - workplace badge removed (now redundant with
                            the Workplace row in the learning overlay)
                        Map remains fully interactive — overlays only
                        block their own footprints, the rest of the map
                        accepts pan / zoom normally. */}

                    {/* Map render. Two possible paths:
                          1) JS SDK interactive map (preferred) — renders an
                             actual Google Maps instance with pan/zoom and
                             programmable markers. Requires
                             REACT_APP_GOOGLE_MAPS_KEY.
                          2) Static Maps PNG fallback — used if no key is
                             configured OR the SDK fails to load (ad-blocker,
                             network failure). Same workplace-pin / candidate-
                             pin behavior, just non-interactive.
                        The wrapper div + the workplace badge overlay are
                        shared between both paths so the visual chrome stays
                        consistent. */}
                    <div className="map-preview-static-wrap">
                      {isInteractiveMapAvailable() && !interactiveMapFailed ? (
                        <InteractiveMap
                          cityQuery={query}
                          candidates={candidates}
                          workplaceCoords={
                            hasWorkplaceCoords
                              ? { lat: workLat, lng: workLng }
                              : null
                          }
                          workplaceLabel={shortWorkplace || ctxWorkplace}
                          onLoadError={() => {
                            console.warn(
                              '[AIChat] Maps JS SDK failed to load — falling ' +
                              'back to Static Maps PNG. Common causes: ' +
                              'ad-blocker, REACT_APP_GOOGLE_MAPS_KEY domain ' +
                              'restriction mismatch, or network failure.'
                            );
                            setInteractiveMapFailed(true);
                          }}
                        />
                      ) : (
                        <>
                          <img
                            className="map-preview-static-img"
                            src={mapsUrl}
                            alt={`Map of ${query}`}
                            loading="lazy"
                            onError={(e) => {
                              console.warn(
                                '[AIChat] static map failed to load — backend ' +
                                'may need a restart to mount /api/places/staticmap, ' +
                                'or GOOGLE_MAPS_API_KEY may be missing. URL: ' +
                                mapsUrl
                              );
                              e.target.style.display = 'none';
                              const fb = e.target.parentNode.querySelector(
                                '.map-preview-static-fallback'
                              );
                              if (fb) fb.style.display = 'flex';
                            }}
                          />
                          <div
                            className="map-preview-static-fallback"
                            style={{ display: 'none' }}
                          >
                            <div className="map-preview-static-fallback-title">
                              Map preview unavailable
                            </div>
                            <div className="map-preview-static-fallback-detail">
                              {query}
                            </div>
                          </div>
                        </>
                      )}
                      {/* Workplace badge removed in Option C — the
                          floating "What I'm learning" overlay now shows
                          the Workplace row. Keeping the badge would
                          duplicate the same info in two places. */}
                    </div>

                    {/* TOP-LEFT FLOATING OVERLAY: What I'm learning
                        + readiness bar. Moved to top per LG feedback
                        2026-05-06 evening (was bottom-right). Captured
                        prefs accumulate as the user chats; readiness
                        bar fills toward 100%. */}
                    <div className="map-preview-overlay map-preview-overlay-learning">
                      <span className="map-preview-eyebrow">
                        What I'm learning
                      </span>
                      <ul className="map-preview-prefs-list">
                        {prefRows.map((row) => (
                          <li key={row.label} className="map-preview-pref-row">
                            <span className="map-preview-pref-icon" aria-hidden="true">
                              {row.icon}
                            </span>
                            <span className="map-preview-pref-label">
                              {row.label}
                            </span>
                            <span className="map-preview-pref-value">
                              {row.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="map-preview-readiness">
                        <div className="map-preview-readiness-meta">
                          <span>{readinessPct}% ready to search</span>
                          <span className="map-preview-readiness-hint">
                            keep chatting →
                          </span>
                        </div>
                        <div className="map-preview-readiness-bar">
                          <div
                            className="map-preview-readiness-fill"
                            style={{ width: `${readinessPct}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* TOP-RIGHT FLOATING OVERLAY: Considering pills.
                        2026-05-06 evening (LG feedback): removed the
                        "N neighborhoods in {city}" caption — redundant
                        with the city already shown in the prefs card
                        and the pins visible on the map below. Just
                        eyebrow + the pill grid. */}
                    {candidates.length > 0 && (
                      <div className="map-preview-overlay map-preview-overlay-considering">
                        <span className="map-preview-eyebrow">
                          Considering
                        </span>
                        <div className="map-preview-candidates-pills">
                          {candidates.map((c) => (
                            <span
                              key={c.name}
                              className="map-preview-candidate-pill"
                            >
                              {c.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Old stacked prefs card removed in Option C — its
                        content (prefs + readiness) lives in the bottom-right
                        floating overlay above. */}
                  </div>
                );
              }
              return (
                <div className="empty-state">
                  <span className="empty-eyebrow">How it works</span>
                  <h2>
                    Let's find your{' '}
                    <span className="empty-headline-accent">perfect place.</span>
                  </h2>
                  <p>
                    Chat with the AI on the left — describe your life, your
                    work commute, what matters to you. We'll do the rest.
                  </p>

                  {/* Round 6 / Tier 2.1 (2026-05-06): replaced 4 small text-only
                      numbered cards + abstract neighborhood SVG with 4 large
                      visual preview cards. Each one teaches the user what that
                      step actually produces — so the empty state pre-sells the
                      experience instead of just describing it.
                      All inline SVG/HTML/CSS, no external assets, no API calls. */}
                  <div className="features-list features-list-rich">
                    {/* ─── 01: AI-powered conversation ──────────────────── */}
                    <div className="feature-card">
                      <div className="feature-card-head">
                        <span className="feature-card-number">01</span>
                        <h3 className="feature-card-title">AI-powered conversation</h3>
                      </div>
                      <p className="feature-card-desc">
                        Just describe your life. The AI figures out the rest.
                      </p>
                      <div className="feature-card-mock fc-mock-chat" aria-hidden="true">
                        <div className="fc-pills-row">
                          <span className="fc-pill">📍 Austin</span>
                          <span className="fc-pill">👨‍👩‍👧 Family</span>
                          <span className="fc-pill">💰 $4k/mo</span>
                        </div>
                        <div className="fc-bubble fc-bubble-user">
                          Moving to Austin with kids. Schools matter most.
                        </div>
                        <div className="fc-bubble fc-bubble-ai">
                          Got it! Top-rated school zones with parks coming up...
                        </div>
                      </div>
                    </div>

                    {/* ─── 02: Personalized fit scoring (radar chart) ───── */}
                    <div className="feature-card">
                      <div className="feature-card-head">
                        <span className="feature-card-number">02</span>
                        <h3 className="feature-card-title">Personalized fit scoring</h3>
                      </div>
                      <p className="feature-card-desc">
                        A match score across the dimensions YOU care about.
                      </p>
                      <div className="feature-card-mock fc-mock-radar" aria-hidden="true">
                        <svg viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
                          {/* Pentagon grid (3 concentric levels) */}
                          {[1, 0.66, 0.33].map((scale, i) => {
                            const cx = 110, cy = 90, r = 60 * scale;
                            const points = [0, 1, 2, 3, 4]
                              .map((idx) => {
                                const angle = (Math.PI / 2) - (idx * 2 * Math.PI / 5);
                                return `${cx + r * Math.cos(angle)},${cy - r * Math.sin(angle)}`;
                              })
                              .join(' ');
                            return (
                              <polygon
                                key={i}
                                points={points}
                                fill="none"
                                stroke="var(--border-light)"
                                strokeWidth="1"
                              />
                            );
                          })}
                          {/* Spokes */}
                          {[0, 1, 2, 3, 4].map((idx) => {
                            const angle = (Math.PI / 2) - (idx * 2 * Math.PI / 5);
                            return (
                              <line
                                key={`spoke-${idx}`}
                                x1="110" y1="90"
                                x2={110 + 60 * Math.cos(angle)}
                                y2={90 - 60 * Math.sin(angle)}
                                stroke="var(--border-light)"
                                strokeWidth="1"
                              />
                            );
                          })}
                          {/* Filled data polygon — sample fit per dimension */}
                          {(() => {
                            const data = [0.92, 0.78, 0.85, 0.95, 0.70]; // Budget, Commute, Lifestyle, Reviews, Walk
                            const points = data
                              .map((val, idx) => {
                                const angle = (Math.PI / 2) - (idx * 2 * Math.PI / 5);
                                const r = 60 * val;
                                return `${110 + r * Math.cos(angle)},${90 - r * Math.sin(angle)}`;
                              })
                              .join(' ');
                            return (
                              <polygon
                                points={points}
                                fill="var(--accent-primary)"
                                fillOpacity="0.25"
                                stroke="var(--accent-primary)"
                                strokeWidth="2"
                              />
                            );
                          })()}
                          {/* Data points */}
                          {(() => {
                            const data = [0.92, 0.78, 0.85, 0.95, 0.70];
                            return data.map((val, idx) => {
                              const angle = (Math.PI / 2) - (idx * 2 * Math.PI / 5);
                              const r = 60 * val;
                              return (
                                <circle
                                  key={`pt-${idx}`}
                                  cx={110 + r * Math.cos(angle)}
                                  cy={90 - r * Math.sin(angle)}
                                  r="3"
                                  fill="var(--accent-primary)"
                                />
                              );
                            });
                          })()}
                          {/* Center match-score badge */}
                          <circle cx="110" cy="90" r="20" fill="var(--accent-primary)" />
                          <text
                            x="110" y="95"
                            textAnchor="middle"
                            fill="var(--text-inverse)"
                            fontSize="13"
                            fontWeight="700"
                          >
                            92%
                          </text>
                          {/* Axis labels */}
                          <text x="110" y="22" textAnchor="middle" fontSize="9" fill="var(--text-secondary)" fontWeight="600">Budget</text>
                          <text x="195" y="62" textAnchor="end" fontSize="9" fill="var(--text-secondary)" fontWeight="600">Commute</text>
                          <text x="178" y="155" textAnchor="end" fontSize="9" fill="var(--text-secondary)" fontWeight="600">Reviews</text>
                          <text x="42" y="155" textAnchor="start" fontSize="9" fill="var(--text-secondary)" fontWeight="600">Lifestyle</text>
                          <text x="25" y="62" textAnchor="start" fontSize="9" fill="var(--text-secondary)" fontWeight="600">Walk</text>
                        </svg>
                      </div>
                    </div>

                    {/* ─── 03: Real resident reviews ────────────────────── */}
                    <div className="feature-card">
                      <div className="feature-card-head">
                        <span className="feature-card-number">03</span>
                        <h3 className="feature-card-title">Real resident reviews</h3>
                      </div>
                      <p className="feature-card-desc">
                        See what people who actually live there say.
                      </p>
                      <div className="feature-card-mock fc-mock-reviews" aria-hidden="true">
                        <div className="fc-quote fc-quote-1">
                          <p>"Quiet streets, great parks for my kids."</p>
                          <span className="fc-quote-author">— Sarah K.</span>
                        </div>
                        <div className="fc-quote fc-quote-2">
                          <p>"Walkable, lots of cafes, feels safe at night."</p>
                          <span className="fc-quote-author">— Mike R.</span>
                        </div>
                        <div className="fc-quote fc-quote-3">
                          <p>"Top schools and a real neighborhood vibe."</p>
                          <span className="fc-quote-author">— Emma L.</span>
                        </div>
                      </div>
                    </div>

                    {/* ─── 04: Ranked neighborhood matches (mock rec card) ─
                         LG feedback 2026-05-06: previous mini-map version was
                         too abstract. Replaced with a literal mock of the real
                         RecommendationCard — same hero + match badge + name +
                         stat pills the user sees in actual results. Direct
                         "this is what you get" preview. */}
                    <div className="feature-card">
                      <div className="feature-card-head">
                        <span className="feature-card-number">04</span>
                        <h3 className="feature-card-title">Ranked neighborhood matches</h3>
                      </div>
                      <p className="feature-card-desc">
                        Each match comes with a fit score and the data behind it.
                      </p>
                      <div className="feature-card-mock fc-mock-card" aria-hidden="true">
                        {/* Mini recommendation card — hero with rank badge + match score */}
                        <div className="fc-rec-card">
                          <div className="fc-rec-hero">
                            {/* Rank badge top-left */}
                            <span className="fc-rec-rank">#1</span>
                            {/* Match-score overlay bottom-left */}
                            <div className="fc-rec-match">
                              <span className="fc-rec-match-num">92</span>
                              <span className="fc-rec-match-label">MATCH</span>
                            </div>
                          </div>
                          <div className="fc-rec-body">
                            <div className="fc-rec-name">Mueller</div>
                            <div className="fc-rec-stats">
                              <span className="fc-rec-stat">
                                <span className="fc-rec-stat-icon">🏫</span>
                                9.4
                              </span>
                              <span className="fc-rec-stat">
                                <span className="fc-rec-stat-icon">🚶</span>
                                87
                              </span>
                              <span className="fc-rec-stat">
                                <span className="fc-rec-stat-icon">💰</span>
                                $2.8k
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Hint that there's more below — 2nd & 3rd ghost cards */}
                        <div className="fc-rec-ghost fc-rec-ghost-2">
                          <span className="fc-rec-ghost-rank">#2</span>
                          <span className="fc-rec-ghost-name">Travis Heights</span>
                          <span className="fc-rec-ghost-pct">88%</span>
                        </div>
                        <div className="fc-rec-ghost fc-rec-ghost-3">
                          <span className="fc-rec-ghost-rank">#3</span>
                          <span className="fc-rec-ghost-name">Crestview</span>
                          <span className="fc-rec-ghost-pct">81%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })())}
            {panelView === 'recommendations' && recommendations && (
              <div className="recommendations-container">
                <div className="recommendations-header">
                  <span className="recommendations-eyebrow">Your matches</span>
                  <h2>
                    Top{' '}
                    <span className="recommendations-headline-accent">
                      {recommendations.recommendations?.length || 0} neighborhoods
                    </span>{' '}
                    for you
                  </h2>
                  <p className="results-summary">
                    {recommendations.summary ||
                      `Ranked by how well each one fits your priorities.`}
                  </p>
                </div>

                <div className="recommendations-list">
                  {recommendations.recommendations && recommendations.recommendations.length > 0 ? (
                    recommendations.recommendations.map((rec, index) => {
                      // Build search-context payload for the feedback API
                      // (added 2026-04-25 with Block 3 thumbs wiring).
                      // Field locations defensive — backend has shipped slight
                      // structure variations across the rebuild, so we look in
                      // a few places for city/state/persona/intent.
                      const prefs = recommendations.userPreferences || {};
                      // Resolve state to 2-letter code. The backend stores the
                      // full state name ("Texas") in extractedData.location.state.
                      // toStateCode() handles either "TX" or "Texas" cleanly.
                      const rawState =
                        prefs.location?.stateShort ||
                        prefs.location?.state ||
                        prefs.stateShort ||
                        prefs.state ||
                        rec.rawData?.state ||
                        null;
                      const stateCode = toStateCode(rawState);
                      const searchContext = {
                        city:
                          prefs.location?.value ||
                          prefs.location?.city ||
                          prefs.city ||
                          rec.rawData?.city ||
                          null,
                        state: stateCode,
                        matchedPersona:
                          recommendations.matchedPersona ||
                          prefs.matchedPersona ||
                          null,
                        housingIntent:
                          prefs.housingIntent ||
                          recommendations.housingIntent ||
                          null
                      };
                      return (
                        <RecommendationCard
                          key={rec.neighborhood || index}
                          recommendation={rec}
                          rank={rec.rank || index + 1}
                          userPriorities={prefs.priorities || []}
                          sessionId={sessionId}
                          searchContext={searchContext}
                          onPanelOpen={handlePanelOpen}
                        />
                      );
                    })
                  ) : (
                    <div className="no-results">
                      <p>No recommendations found. Please try adjusting your preferences.</p>
                    </div>
                  )}
                </div>

                {/* Top Recommendation Highlight — restyled to splash indigo
                    glow gradient (was yellow). Sunday polish 2026-05-03. */}
                {recommendations.topRecommendation && (
                  <div className="top-pick-banner">
                    <div className="top-pick-glow" aria-hidden="true" />
                    <div className="banner-content">
                      <span className="banner-eyebrow">Our top pick</span>
                      <div className="banner-text">
                        {recommendations.topRecommendation}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ─── Footer — splash-aligned ─────────────────────────────────
          Same minimal style as the splash footer: light copy, single
          right-aligned share-review link. Hidden on mobile via CSS. */}
      <footer className="ai-footer">
        <p className="ai-footer-copy">
          Powered by the LocalGenie multi-agent system.
        </p>
        <div className="ai-footer-links">
          <Link to="/submit-review" className="ai-footer-link">
            Share a review
          </Link>
          <Link to="/feedback" className="ai-footer-link">
            Send feedback
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default AIChat;
